import { Layout } from "@/components/layout/Layout";
import { ScrollFadeIn } from "@/components/ScrollFadeIn";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { RollingBarSection } from "@/components/rolling-bar/RollingBarSection";

const BG_IMAGE = "/cases/madame-drouot/hero.jpg";

/* ─── Persistent background layer ─────────────────────────────────── */
function PageBackground() {
  return (
    <div className="fixed inset-0 -z-10">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url('${BG_IMAGE}')` }}
      />
      <div className="absolute inset-0 bg-background/[0.82]" />
    </div>
  );
}

/* ─── Section 1 · Hero ──────────────────────────────────────────────── */
function Hero() {
  return (
    <section className="relative flex min-h-[85vh] items-center justify-center px-6 py-32 overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url('${BG_IMAGE}')` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/50 to-background/90" />
      <div className="relative z-10 mx-auto max-w-3xl text-center">
        <ScrollFadeIn>
          <h1 className="font-serif text-4xl leading-tight tracking-tight text-foreground md:text-5xl lg:text-6xl">
            Lighting Systems, aligned&nbsp;with&nbsp;the&nbsp;space.
          </h1>
        </ScrollFadeIn>
        <ScrollFadeIn delay={120}>
          <p className="mx-auto mt-8 max-w-2xl font-sans text-base leading-relaxed text-muted-foreground md:text-lg">
            Lighting is not just about fixtures. It is about atmosphere, control,
            integration, and how a space is actually experienced. MINZO helps
            designers source lighting systems that hold both visual intent and
            technical coherence.
          </p>
        </ScrollFadeIn>
        <ScrollFadeIn delay={240}>
          <Button asChild size="lg" className="mt-10 uppercase tracking-widest text-xs font-sans">
            <Link to="/#concierge">
              Start an inquiry <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Link>
          </Button>
        </ScrollFadeIn>
      </div>
    </section>
  );
}

/* ─── Section 2 · Intro ─────────────────────────────────────────────── */
function Intro() {
  return (
    <section className="relative px-6 py-24 md:py-32">
      <div className="absolute inset-0 bg-background/60 backdrop-blur-sm" />
      <div className="relative mx-auto max-w-[1100px]">
        <ScrollFadeIn>
          <div className="mx-auto max-w-3xl">
            <h2 className="font-serif text-3xl text-foreground md:text-4xl">
              More than selecting lights.
            </h2>
            <div className="mt-8 space-y-5 font-sans text-base leading-relaxed text-muted-foreground md:text-[17px]">
              <p>
                Lighting often breaks down when treated as a product category rather
                than a system. Wrong beam angles flatten surfaces. Mismatched color
                temperatures create visual tension. Dimming becomes unstable.
                Controls remain disconnected. Decorative and architectural lighting
                feel like they belong to different projects.
              </p>
              <p>
                MINZO helps designers source lighting as a whole system — so every
                layer, from recessed infrastructure to decorative statements, works
                together with precision and coherence.
              </p>
            </div>
          </div>
        </ScrollFadeIn>
      </div>
    </section>
  );
}

/* ─── Section 3 · What We Support ────────────────────────────────── */
const supports = [
  {
    title: "Architectural Lighting",
    body: "Recessed, linear, track, and surface-mounted systems — selected for optical quality, material finish, and integration into ceiling and wall conditions.",
  },
  {
    title: "Decorative Lighting",
    body: "Pendants, wall lights, floor and table fixtures — sourced to complement the spatial narrative and material palette without competing for attention.",
  },
  {
    title: "Control & Dimming",
    body: "Protocol selection, dimming compatibility, scene control, and integration with architectural switching — specified to match the project's technical infrastructure.",
  },
  {
    title: "Project Coordination",
    body: "Cross-referencing product selections, confirming lead times, aligning with electrical consultants, and ensuring that what is specified is what arrives on site.",
  },
];

function WhatWeSupport() {
  return (
    <section className="relative border-t border-border/40 px-6 py-24 md:py-32">
      <div className="absolute inset-0 bg-background/75 backdrop-blur-[2px]" />
      <div className="relative mx-auto max-w-[1100px]">
        <ScrollFadeIn>
          <h2 className="font-serif text-3xl text-foreground md:text-4xl">
            What we support
          </h2>
        </ScrollFadeIn>
        <div className="mt-16 grid gap-12 sm:grid-cols-2">
          {supports.map((s, i) => (
            <ScrollFadeIn key={s.title} delay={i * 100}>
              <div>
                <h3 className="font-serif text-xl text-foreground">{s.title}</h3>
                <p className="mt-3 font-sans text-sm leading-relaxed text-muted-foreground md:text-base">
                  {s.body}
                </p>
              </div>
            </ScrollFadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Section 4 · Why System Thinking Matters ────────────────────── */
function WhySystemThinking() {
  return (
    <section className="relative px-6 py-24 md:py-32">
      <div className="absolute inset-0 bg-background/60 backdrop-blur-sm" />
      <div className="relative mx-auto max-w-[1100px]">
        <ScrollFadeIn>
          <div className="mx-auto max-w-3xl">
            <h2 className="font-serif text-3xl text-foreground md:text-4xl">
              Why system thinking matters
            </h2>
            <div className="mt-8 space-y-5 font-sans text-base leading-relaxed text-muted-foreground md:text-[17px]">
              <p>
                A good fixture alone does not guarantee a good result. Light
                distribution, glare control, dimming behaviour, control protocol,
                ceiling conditions — each layer introduces variables that affect the
                experience of a space.
              </p>
              <p>
                The balance between ambient, accent, task, and decorative lighting
                determines whether a room feels intentional or assembled. System
                thinking means treating every light source as part of a coordinated
                whole — not a list of separate product decisions.
              </p>
            </div>
          </div>
        </ScrollFadeIn>
      </div>
    </section>
  );
}

/* ─── Section 5 · Typical Problems ───────────────────────────────── */
const problems = [
  "Visual concept is clear, but the technical lighting path is not.",
  "Fixture language is fragmented across multiple suppliers.",
  "Uncertainty around dimming stability and protocol compatibility.",
  "Architectural and decorative lighting feel disconnected.",
  "Local sourcing options do not match the project quality level.",
  "Lighting works functionally — but not atmospherically.",
];

function TypicalProblems() {
  return (
    <section className="relative border-t border-border/40 px-6 py-24 md:py-32">
      <div className="absolute inset-0 bg-background/75 backdrop-blur-[2px]" />
      <div className="relative mx-auto max-w-[1100px]">
        <ScrollFadeIn>
          <h2 className="font-serif text-3xl text-foreground md:text-4xl">
            Typical problems we help solve
          </h2>
        </ScrollFadeIn>
        <ul className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {problems.map((p, i) => (
            <ScrollFadeIn key={i} delay={i * 80}>
              <li className="flex items-start gap-3 font-sans text-sm leading-relaxed text-muted-foreground md:text-base">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/50" />
                {p}
              </li>
            </ScrollFadeIn>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ─── Section 6 · How We Work ────────────────────────────────────── */
const steps = [
  { num: "01", title: "Understand the spatial intent", body: "We begin with the project's atmosphere, material palette, and spatial proportions — not a product catalogue." },
  { num: "02", title: "Define the lighting layers", body: "Mapping architectural, accent, task, and decorative layers to understand what each zone requires." },
  { num: "03", title: "Match products and systems", body: "Sourcing fixtures, drivers, and controls that align technically and visually — as a coordinated system." },
  { num: "04", title: "Coordinate for execution", body: "Aligning lead times, confirming specifications, and supporting delivery through to site installation." },
];

function HowWeWork() {
  return (
    <section className="relative px-6 py-24 md:py-32">
      <div className="absolute inset-0 bg-background/60 backdrop-blur-sm" />
      <div className="relative mx-auto max-w-[1100px]">
        <ScrollFadeIn>
          <h2 className="font-serif text-3xl text-foreground md:text-4xl">
            How we work
          </h2>
        </ScrollFadeIn>
        <div className="mt-16 grid gap-12 sm:grid-cols-2">
          {steps.map((s, i) => (
            <ScrollFadeIn key={s.num} delay={i * 100}>
              <div>
                <span className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-primary/60">
                  {s.num}
                </span>
                <h3 className="mt-2 font-serif text-xl text-foreground">{s.title}</h3>
                <p className="mt-3 font-sans text-sm leading-relaxed text-muted-foreground md:text-base">
                  {s.body}
                </p>
              </div>
            </ScrollFadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Section 7 · System Areas ───────────────────────────────────── */
const areas = [
  "Recessed downlights",
  "Adjustable spotlights",
  "Linear lighting",
  "Wall grazing / washing",
  "Cove lighting",
  "Decorative pendants",
  "Wall lights",
  "Exterior architectural lighting",
  "Dimming systems",
  "Smart lighting integration",
];

function SystemAreas() {
  return (
    <section className="relative border-t border-border/40 px-6 py-24 md:py-32">
      <div className="absolute inset-0 bg-background/75 backdrop-blur-[2px]" />
      <div className="relative mx-auto max-w-[1100px]">
        <ScrollFadeIn>
          <h2 className="font-serif text-3xl text-foreground md:text-4xl">
            System areas
          </h2>
        </ScrollFadeIn>
        <div className="mt-14 flex flex-wrap gap-3">
          {areas.map((a, i) => (
            <ScrollFadeIn key={a} delay={i * 50}>
              <span className="inline-block border border-border/60 bg-background/50 backdrop-blur-sm px-5 py-2.5 font-sans text-xs uppercase tracking-[0.12em] text-foreground/70">
                {a}
              </span>
            </ScrollFadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Section 8 · Brand Positioning ──────────────────────────────── */
function BrandPositioning() {
  return (
    <section className="relative px-6 py-24 md:py-32">
      <div className="absolute inset-0 bg-background/60 backdrop-blur-sm" />
      <div className="relative mx-auto max-w-[1100px]">
        <ScrollFadeIn>
          <div className="mx-auto max-w-3xl">
            <h2 className="font-serif text-3xl text-foreground md:text-4xl">
              For designers who care about precision,&nbsp;not&nbsp;excess
            </h2>
            <p className="mt-8 font-sans text-base leading-relaxed text-muted-foreground md:text-[17px]">
              The right lighting system should reveal material texture, support mood,
              and remain visually controlled. It should feel intentional, quiet, and
              integrated into the architecture — not layered on top of it.
            </p>
          </div>
        </ScrollFadeIn>
      </div>
    </section>
  );
}

/* ─── Section 9 · Final CTA ──────────────────────────────────────── */
function FinalCTA() {
  return (
    <section className="relative border-t border-border/40 px-6 py-24 md:py-32">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url('${BG_IMAGE}')` }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/70 to-background/80" />
      <div className="relative mx-auto max-w-3xl text-center">
        <ScrollFadeIn>
          <h2 className="font-serif text-3xl text-foreground md:text-4xl">
            Working on a lighting-led project?
          </h2>
        </ScrollFadeIn>
        <ScrollFadeIn delay={100}>
          <p className="mx-auto mt-6 max-w-xl font-sans text-base leading-relaxed text-muted-foreground">
            Share your drawings, references, or current questions. We can support
            fixture selection, control logic, and integrated sourcing based on the
            needs of the project.
          </p>
        </ScrollFadeIn>
        <ScrollFadeIn delay={200}>
          <Button asChild size="lg" className="mt-10 uppercase tracking-widest text-xs font-sans">
            <Link to="/#concierge">
              Start a lighting inquiry <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Link>
          </Button>
        </ScrollFadeIn>
      </div>
    </section>
  );
}

/* ─── Page ────────────────────────────────────────────────────────── */
export default function LightingSystems() {
  return (
    <Layout>
      <PageBackground />
      <Hero />
      <Intro />
      <WhatWeSupport />
      <WhySystemThinking />
      <TypicalProblems />
      <HowWeWork />
      <SystemAreas />
      <BrandPositioning />
      <RollingBarSection />
      <FinalCTA />
    </Layout>
  );
}
